import type { ReactNode } from 'react'
import type { BookedService, BookingBill, ServiceAdjustment } from '../../types/storefront'
import { Divider } from '../../components/ui'
import { formatDuration, formatMoney, type Currency } from '../../utils/format'

/**
 * The bill of an appointment, line for line what the salon's dashboard prints for it:
 * each service at the price charged with the surcharges that got it there, then the
 * payment summary. The figures come from the server as they are — nothing is summed
 * here — so the customer and the salon always read the same total.
 */

/** Same wording as the dashboard. */
function adjustmentLabel(adj: ServiceAdjustment): string {
  if (adj.type === 'percentage') {
    return adj.reason ? `Service fee ${adj.amount}% · ${adj.reason}` : `Service fee ${adj.amount}%`
  }
  return adj.reason || 'Additional charge'
}

export function ServiceLines({ services, currency }: { services: BookedService[]; currency: Currency }) {
  return (
    <ul className="flex flex-col gap-5">
      {services.map((service, index) => {
        const adjustments = service.adjustments ?? []
        return (
          <li key={`${service.id}-${index}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-body-md text-body-md text-on-surface">{service.name}</p>
                <p className="mt-1 font-body-md text-sm text-on-surface-variant">
                  {formatDuration(service.duration)}
                  {service.staff_name ? ` · ${service.staff_name}` : ''}
                </p>
              </div>
              <span className="whitespace-nowrap font-body-md text-body-md text-on-surface">
                {formatMoney(service.price, currency)}
              </span>
            </div>
            {adjustments.length > 0 && (
              <div className="mt-2 flex flex-col gap-1 font-body-md text-sm text-on-surface-variant">
                {adjustments.map((adj) => (
                  <div key={adj.id} className="flex items-center justify-between gap-4">
                    <span className="min-w-0 truncate">{adjustmentLabel(adj)}</span>
                    <span className="whitespace-nowrap tabular-nums">+{formatMoney(adj.computed_amount, currency)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 opacity-70">
                  <span>Booked at</span>
                  <span className="whitespace-nowrap tabular-nums">
                    {formatMoney(service.base_price ?? service.price, currency)}
                  </span>
                </div>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function Row({ label, children, strong = false }: { label: ReactNode; children: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={
          strong
            ? 'font-label-caps text-label-caps uppercase text-on-surface-variant'
            : 'font-body-md text-body-md text-on-surface-variant'
        }
      >
        {label}
      </span>
      {children}
    </div>
  )
}

/** The dashboard's payment summary: same rows, same order, same conditions. */
export function PaymentSummary({ bill, currency }: { bill: BookingBill; currency: Currency }) {
  const money = (n: number) => formatMoney(n, currency)
  const value = (text: string, className = 'text-on-surface') => (
    <span className={`whitespace-nowrap font-body-md text-body-md tabular-nums ${className}`}>{text}</span>
  )
  const surcharge = bill.adjustments_total

  return (
    <div className="flex flex-col gap-3">
      {bill.payment_type && (
        <Row label="Payment type">
          <span className="rounded-full bg-primary px-3 py-0.5 font-body-md text-sm capitalize text-on-primary">
            {bill.payment_type}
          </span>
        </Row>
      )}
      <Row label="Service amount">{value(money(bill.sub_total_original))}</Row>
      {surcharge > 0 && <Row label="Additional charges & fees">{value(`+${money(surcharge)}`)}</Row>}
      <Row label={surcharge > 0 ? 'Adjusted subtotal' : 'Subtotal'}>{value(money(bill.sub_total))}</Row>
      {bill.taxes.map((tax, index) => (
        <Row key={`${tax.name}-${index}`} label={`${tax.name}: ${tax.percent}%`}>
          {value(money(tax.amount))}
        </Row>
      ))}
      {bill.taxes.length === 0 && bill.tax_amount > 0 && <Row label="Tax">{value(money(bill.tax_amount))}</Row>}
      <Row label="Tip amount">{value(money(bill.tip))}</Row>
      {bill.discount > 0 && (
        <Row label={`Discount${bill.coupon_code ? ` (${bill.coupon_code})` : ''}`}>
          {value(`−${money(bill.discount)}`, 'text-primary')}
        </Row>
      )}

      <Divider className="my-3" />

      <Row label="Total" strong>
        <span className="font-display text-headline-sm text-primary">{money(bill.total)}</span>
      </Row>

      {/* Taxes and tip are only settled when the salon takes payment. */}
      {!bill.payment_type && (
        <p className="font-body-md text-sm text-on-surface-variant">
          Taxes and tip are added when you pay at the salon.
        </p>
      )}
    </div>
  )
}
